import { Door01Icon, Store01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { api } from "@slapinc/slappos-backend/api";
import {
	createFileRoute,
	useNavigate,
	useSearch,
} from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import type { GenericId as Id } from "convex/values";
import { useCallback, useState } from "react";
import { BranchSelector } from "@/components/branch-selector";
import { Can } from "@/components/can";
import { PageContent } from "@/components/layout/page-content";
import { CreateRoomModal } from "@/components/modals/create-room-modal";
import { EditRoomModal } from "@/components/modals/edit-room-modal";
import { ActionHeaderButton } from "@/components/ui/action-header-button";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { EmptyStateWithSkeleton } from "@/components/ui/empty-state-with-skeleton";
import { RoomTable } from "@/features/rooms/_components/room-table";
import { useAutoSelectBranch } from "@/hooks/use-auto-select-branch";
import { useMe } from "@/hooks/use-me";
import { usePagination } from "@/hooks/use-pagination";
import { handleConvexError } from "@/lib/errors";
import type { RoomFormInput } from "@/lib/schemas/room";
import { toast } from "@/lib/toast";
import type { Room } from "@/lib/types/room";

interface RoomsSearch {
	branch?: string;
}

export const Route = createFileRoute("/(dashboard)/branch/rooms")({
	component: RoomsPage,
	validateSearch: (search: Record<string, unknown>): RoomsSearch => ({
		branch: search.branch as string | undefined,
	}),
});

const PAGE_SIZE = 10;

function RoomsPage() {
	const navigate = useNavigate();
	const { branch: searchBranchId } = useSearch({
		from: "/(dashboard)/branch/rooms",
	});

	// Pagination
	const { pagination, onPaginationChange, resetPage } =
		usePagination(PAGE_SIZE);

	// Modal state
	const [createModalOpen, setCreateModalOpen] = useState(false);
	const [editModalOpen, setEditModalOpen] = useState(false);
	const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [roomToDelete, setRoomToDelete] = useState<Room | null>(null);

	// Loading states
	const [isCreating, setIsCreating] = useState(false);
	const [isUpdating, setIsUpdating] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [branchSelectorOpen, setBranchSelectorOpen] = useState(false);

	// Get current user for org context
	const me = useMe();
	const orgId = me?.activeOrganizationId;

	const branchesData = useQuery(
		api.features.branches.web.list,
		orgId ? { orgId } : "skip",
	);
	const branches = branchesData ?? [];

	const { branchId, isLoading: isLoadingBranches } = useAutoSelectBranch({
		branchId: searchBranchId,
		branches: branchesData,
		orgId,
	});

	const roomsData = useQuery(
		api.features.rooms.web.list,
		orgId && branchId
			? { orgId, branchId: branchId as Id<"branches"> }
			: "skip",
	);

	const rooms = (roomsData ?? []) as Room[];
	const isLoading =
		isLoadingBranches || (roomsData === undefined && !!branchId);

	// Mutations
	const createRoom = useMutation(api.features.rooms.web.create);
	const updateRoom = useMutation(api.features.rooms.web.update);
	const removeRoom = useMutation(api.features.rooms.web.remove);

	// Handle branch selection
	const handleBranchChange = useCallback(
		(value: Id<"branches"> | Id<"branches">[] | undefined) => {
			if (typeof value !== "string") return;
			navigate({
				to: ".",
				search: (prev: RoomsSearch) => ({ ...prev, branch: value }),
			});
			resetPage();
		},
		[navigate, resetPage],
	);

	// Handlers
	const handleCreate = useCallback(
		async (data: RoomFormInput) => {
			if (!orgId || !branchId) return;

			setIsCreating(true);
			try {
				await createRoom({
					orgId,
					branchId: branchId as Id<"branches">,
					name: data.name,
					description: data.description || undefined,
					forSale: data.forSale,
					isDefault: data.isDefault,
					isActive: data.isActive,
				});
				toast.success(<>Room added</>);
			} catch (error) {
				handleConvexError(error, "Failed to add room");
				throw error;
			} finally {
				setIsCreating(false);
			}
		},
		[orgId, branchId, createRoom],
	);

	const handleEdit = useCallback((room: Room) => {
		setSelectedRoom(room);
		setEditModalOpen(true);
	}, []);

	const handleUpdate = useCallback(
		async (data: RoomFormInput) => {
			if (!orgId || !branchId || !selectedRoom) return;

			setIsUpdating(true);
			try {
				await updateRoom({
					orgId,
					branchId: branchId as Id<"branches">,
					roomId: selectedRoom._id,
					name: data.name,
					description: data.description || undefined,
					forSale: data.forSale,
					isDefault: data.isDefault,
					isActive: data.isActive,
				});
				toast.success(<>Room updated</>);
			} catch (error) {
				handleConvexError(error, "Failed to update room");
				throw error;
			} finally {
				setIsUpdating(false);
			}
		},
		[orgId, branchId, selectedRoom, updateRoom],
	);

	const handleDelete = useCallback((room: Room) => {
		setRoomToDelete(room);
		setDeleteDialogOpen(true);
	}, []);

	const confirmDelete = useCallback(async () => {
		if (!orgId || !branchId || !roomToDelete) return;

		setIsDeleting(true);
		try {
			await removeRoom({
				orgId,
				branchId: branchId as Id<"branches">,
				roomId: roomToDelete._id,
			});
			toast.success(<>Room removed</>);
			setDeleteDialogOpen(false);
			setRoomToDelete(null);
		} catch (error) {
			handleConvexError(error, "Failed to remove room");
		} finally {
			setIsDeleting(false);
		}
	}, [orgId, branchId, roomToDelete, removeRoom]);

	return (
		<PageContent
			title="Rooms"
			controls={
				<div className="flex items-center gap-3">
					<BranchSelector
						branches={branches}
						value={branchId as Id<"branches"> | undefined}
						onValueChange={handleBranchChange}
						placeholder="Select branch"
						className="w-48"
						open={branchSelectorOpen}
						onOpenChange={setBranchSelectorOpen}
						onViewAll={() => navigate({ to: "/settings/branches" })}
						onCreateNew={() => navigate({ to: "/settings/branches" })}
					/>
					{branchId && (
						<Can scope="rooms.create">
							<ActionHeaderButton
								label="Add Room"
								onClick={() => setCreateModalOpen(true)}
							/>
						</Can>
					)}
				</div>
			}
		>
			<div className="flex flex-1 flex-col gap-4 sm:gap-6">
				{!branchId && !isLoadingBranches ? (
					<div className="flex flex-1 flex-col justify-end pb-12">
						<EmptyStateWithSkeleton
							title="No branches found"
							description="Get started by creating your first branch to manage rooms."
							actionLabel="Create branch"
							onAction={() => navigate({ to: "/settings/branches" })}
							icon={
								<HugeiconsIcon
									icon={Store01Icon}
									className="h-5 w-5 text-neutral-700"
									strokeWidth={2.2}
								/>
							}
						/>
					</div>
				) : !isLoading && !!branchId && rooms.length === 0 ? (
					<div className="flex flex-1 flex-col justify-end pb-12">
						<EmptyStateWithSkeleton
							title="No rooms found"
							description="Create rooms to manage your branch layout and track sales by location"
							actionLabel="Add Room"
							onAction={() => setCreateModalOpen(true)}
							icon={
								<HugeiconsIcon
									icon={Door01Icon}
									className="h-5 w-5 text-neutral-700"
									strokeWidth={2.2}
								/>
							}
						/>
					</div>
				) : (
					<RoomTable
						data={rooms}
						loading={isLoading}
						pagination={pagination}
						onPaginationChange={onPaginationChange}
						rowCount={rooms.length}
						onEdit={handleEdit}
						onDelete={handleDelete}
						showPagination={false}
					/>
				)}
			</div>

			{/* Create Modal */}
			<CreateRoomModal
				open={createModalOpen}
				onOpenChange={setCreateModalOpen}
				onSubmit={handleCreate}
				isSubmitting={isCreating}
			/>

			{/* Edit Modal */}
			<EditRoomModal
				open={editModalOpen}
				onOpenChange={setEditModalOpen}
				room={selectedRoom}
				onSubmit={handleUpdate}
				isSubmitting={isUpdating}
			/>

			{/* Delete Confirmation */}
			<AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Remove room?</AlertDialogTitle>
						<AlertDialogDescription>
							Are you sure you want to remove "{roomToDelete?.name}"? This
							action cannot be undone.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
						<AlertDialogAction
							variant="destructive"
							onClick={confirmDelete}
							disabled={isDeleting}
						>
							{isDeleting ? "Removing..." : "Remove"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</PageContent>
	);
}
